import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedClient } from '../../hooks/useSelectedClient';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import Skeleton from 'react-loading-skeleton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import '../../styles/signature.css';

type Signature = {
  id: string;
  name: string;
};

type StyleOption = {
  key: string;
  value: string; // font family name from NowCerts
};

function dataUrlToBytes(dataUrl: string): number[] {
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
  const binary = atob(base64);
  const bytes: number[] = [];
  for (let i = 0; i < binary.length; i++) {
    bytes.push(binary.charCodeAt(i));
  }
  return bytes;
}

async function textToDataUrl(text: string, fontFamily: string): Promise<string> {
  // Wait for the font to be available before drawing to canvas.
  // Without this, canvas falls back to the browser default font.
  try { await document.fonts.load(`52px "${fontFamily}"`); } catch {}
  const canvas = document.createElement('canvas');
  canvas.width = 520;
  canvas.height = 120;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `52px "${fontFamily}"`;
  ctx.fillStyle = '#1f2937';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, 62);
  return canvas.toDataURL('image/png');
}

// Map NowCerts Windows system font names → loaded Google Fonts equivalents.
// The browser cannot access local system fonts, so we substitute visually similar web fonts.
const NC_FONT_TO_WEB: Record<string, string> = {
  'Blackadder ITC':      'MedievalSharp',
  'Vladimir Script':     'Alex Brush',
  'Edwardian Script ITC':'Pinyon Script',
  'Freestyle Script':    'Dancing Script',
  'Mistral':             'Pacifico',
  'Pristina':            'Satisfy',
  'Segoe Script':        'Kalam',
};

const API_BASE = process.env.REACT_APP_COIPortal_Url;

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
    'Content-Type': 'application/json',
    NCToken: localStorage.getItem('NCToken') || '',
  };
}

export default function SignaturePage() {
  const toastRef = useRef<Toast>(null);
  const navigate = useNavigate();
  const { insuredId } = useSelectedClient();

  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editingSignature, setEditingSignature] = useState<Signature | null>(null);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  const [styleOptions, setStyleOptions] = useState<StyleOption[]>([]);
  const [name, setName] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [textSignature, setTextSignature] = useState('');
  const [textStyle, setTextStyle] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string>('');
  const [isDraggingUpload, setIsDraggingUpload] = useState(false);
  const [drawDataUrl, setDrawDataUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Signature | null>(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState('');
  const [editPreviewLoading, setEditPreviewLoading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewModal, setPreviewModal] = useState<{ open: boolean; name: string; url: string; loading: boolean }>({
    open: false, name: '', url: '', loading: false,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // ── Fetch signatures from API ──
  const fetchSignatures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/igigcoi/signatures`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: Signature[] = await res.json();
      setSignatures(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load signatures. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch style options from API ──
  const fetchStyleOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/igigcoi/signature-styles`, { headers: authHeaders() });
      if (!res.ok) return;
      const data: StyleOption[] = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        setStyleOptions(data);
        setTextStyle(prev => prev || data[0].key);
      }
    } catch {
      // styles are non-critical — silently ignore
    }
  }, []);

  useEffect(() => {
    fetchSignatures();
    fetchStyleOptions();
  }, [fetchSignatures, fetchStyleOptions]);

  const ncFontName = styleOptions.find(s => s.key === textStyle)?.value ?? '';
  const currentFontFamily = NC_FONT_TO_WEB[ncFontName] ?? (ncFontName || 'cursive');

  const filteredSignatures = signatures.filter(s =>
    !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const pagedSignatures = filteredSignatures.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


  // ── Canvas drawing ──
  const getCanvasPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * (canvas.width / rect.width), y: (e.clientY - rect.top) * (canvas.height / rect.height) };
  };

  const handleCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Capture the pointer so mousemove keeps firing even when cursor leaves the canvas
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { ctx.beginPath(); const p = getCanvasPos(e); ctx.moveTo(p.x, p.y); }
  };

  const handleCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { const p = getCanvasPos(e); ctx.lineWidth = 2; ctx.strokeStyle = '#1f2937'; ctx.lineCap = 'round'; ctx.lineTo(p.x, p.y); ctx.stroke(); }
  };

  const handleCanvasPointerUp = () => {
    setIsDrawing(false);
    if (canvasRef.current) setDrawDataUrl(canvasRef.current.toDataURL());
  };

  const handleClearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) { canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height); setDrawDataUrl(''); }
  };

  const processUploadedFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result as string || '');
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processUploadedFile(file);
  };

  const handleUploadDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDraggingUpload(true);
  };

  const handleUploadDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDraggingUpload(false);
  };

  const handleUploadDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDraggingUpload(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processUploadedFile(file);
  };

  const resetForm = () => {
    setName('');
    setActiveTab(0);
    setTextSignature('');
    setTextStyle(styleOptions[0]?.key ?? '');
    setUploadedFile(null);
    setUploadPreview('');
    setDrawDataUrl('');
    setEditingSignature(null);
    setEditPreviewUrl('');
    setEditPreviewLoading(false);
  };

  const handleCreateNew = () => { resetForm(); setView('create'); };

  const handleEdit = async (sig: Signature) => {
    resetForm();
    setEditingSignature(sig);
    setName(sig.name);
    setView('edit');
    setEditPreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/igigcoi/signature-preview/${sig.id}`, { headers: authHeaders() });
      if (res.ok) {
        const url = await res.text();
        setEditPreviewUrl(url.trim().replace(/^"|"$/g, ''));
      }
    } catch {}
    setEditPreviewLoading(false);
  };

  const handleCancel = () => { resetForm(); setView('list'); };

  // ── Save — NowCerts payload: signatureId + signatureBytes + signatureName ──
  const handleSave = async () => {
    if (!name.trim()) {
      toastRef.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Signature name is required', life: 3000 });
      return;
    }

    let signatureBytes: number[] = [];

    if (activeTab === 0) {
      if (!textSignature.trim()) {
        toastRef.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please enter signature text', life: 3000 });
        return;
      }
      const dataUrl = await textToDataUrl(textSignature, currentFontFamily);
      signatureBytes = dataUrlToBytes(dataUrl);
    } else if (activeTab === 1) {
      if (!uploadPreview) {
        toastRef.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please upload a signature image', life: 3000 });
        return;
      }
      signatureBytes = dataUrlToBytes(uploadPreview);
    } else if (activeTab === 2) {
      if (!drawDataUrl) {
        toastRef.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please draw a signature', life: 3000 });
        return;
      }
      signatureBytes = dataUrlToBytes(drawDataUrl);
    }

    setSaving(true);
    try {
      const payload = {
        signatureId: editingSignature?.id ?? '',
        signatureBytes,
        signatureName: name,
        insuredDatabaseId: insuredId ?? '',
      };

      const res = await fetch(`${API_BASE}/api/igigcoi/signatures`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => `HTTP ${res.status}`);
        throw new Error(msg || `HTTP ${res.status}`);
      }

      toastRef.current?.show({ severity: 'success', summary: 'Saved', detail: `Signature ${editingSignature ? 'updated' : 'created'} successfully`, life: 3000 });
      resetForm();
      setView('list');
      fetchSignatures();
    } catch (err: any) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save signature', life: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (sig: Signature) => {
    setDeleteTarget(sig);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const qp = new URLSearchParams();
      if (deleteTarget.name) qp.set('name', deleteTarget.name);
      if (insuredId) qp.set('insuredDatabaseId', insuredId);
      const params = qp.toString() ? `?${qp.toString()}` : '';
      const res = await fetch(`${API_BASE}/api/igigcoi/signatures/${deleteTarget.id}${params}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toastRef.current?.show({ severity: 'success', summary: 'Deleted', detail: `"${deleteTarget.name}" was deleted`, life: 3000 });
      setDeleteTarget(null);
      fetchSignatures();
    } catch {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete signature', life: 3000 });
    } finally {
      setDeleting(false);
    }
  };

  const openPreview = (sig: Signature) => {
    // If already cached, open immediately
    if (previewUrls[sig.id]) {
      setPreviewModal({ open: true, name: sig.name, url: previewUrls[sig.id], loading: false });
      return;
    }
    setPreviewModal({ open: true, name: sig.name, url: '', loading: true });
    fetch(`${API_BASE}/api/igigcoi/signature-preview/${sig.id}`, { headers: authHeaders() })
      .then(res => res.ok ? res.text() : Promise.reject())
      .then(url => {
        const clean = url.trim().replace(/^"|"$/g, '');
        setPreviewUrls(prev => ({ ...prev, [sig.id]: clean }));
        setPreviewModal(prev => ({ ...prev, url: clean, loading: false }));
      })
      .catch(() => setPreviewModal(prev => ({ ...prev, loading: false, url: '' })));
  };

  const dropdownStyleOptions = styleOptions.map(s => ({ label: s.value, value: s.key }));

  // ── List View ──
  const renderListView = () => (
    <div className="sig__listPage">
      <div className="sig__listHeader">
        <div className="sig__listHeaderLeft">
          <button className="sig__backBtn" onClick={() => navigate('/dashboard')}>
            <i className="pi pi-arrow-left" />
          </button>
          <div>
            <h1 className="sig__listTitle">Signatures</h1>
            <p className="sig__listSubtitle">
              {loading ? 'Loading…' : `${filteredSignatures.length} signature${filteredSignatures.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <Button icon="pi pi-plus" label="Create Signature" className="p-button-sm sig__primaryBtn" onClick={handleCreateNew} />
      </div>

      <div className="sig__listFilters">
        <div className="sig__listFilterField">
          <label className="sig__listFilterLabel">Search</label>
          <span className="p-input-icon-left sig__searchWrap">
            <i className="pi pi-search" />
            <InputText maxLength={100}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              placeholder="Search signatures…"
              className="sig__searchInput"
            />
          </span>
        </div>
      </div>

      <div className="sig__tableWrap">
        {loading ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#64748b' }}>Name</th>
                <th style={{ width: 110, padding: '10px 16px', fontWeight: 600, fontSize: 12, color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '13px 16px' }}><Skeleton width={200} height={13} borderRadius={4} /></td>
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <Skeleton width={28} height={28} borderRadius={6} />
                      <Skeleton width={28} height={28} borderRadius={6} />
                      <Skeleton width={28} height={28} borderRadius={6} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : error ? (
          <div className="sig__empty sig__empty--error"><i className="pi pi-exclamation-circle" /><span>{error}</span></div>
        ) : filteredSignatures.length === 0 ? (
          <div className="sig__empty">
            <i className="pi pi-pen-to-square" />
            <p>{searchQuery ? 'No signatures match your search.' : 'No signatures yet.'}</p>
            {!searchQuery && <Button label="Create Your First Signature" icon="pi pi-plus" className="p-button-sm sig__primaryBtn" style={{ marginTop: 12 }} onClick={handleCreateNew} />}
          </div>
        ) : (
          <DataTable value={pagedSignatures} className="sig__dataTable" size="small">
            <Column field="name" header="Name" body={(r) => <span className="sig__cellName">{r.name}</span>} />
            <Column header="Actions" style={{ width: '110px' }}
              body={(r) => (
                <div className="sig__actions">
                  <button className="sig__actionBtn sig__actionBtn--preview" title="Preview" onClick={() => openPreview(r)}><i className="pi pi-eye" /></button>
                  <button className="sig__actionBtn sig__actionBtn--edit" title="Edit" onClick={() => handleEdit(r)}><i className="pi pi-pencil" /></button>
                  <button className="sig__actionBtn sig__actionBtn--delete" title="Delete" onClick={() => handleDelete(r)}><i className="pi pi-trash" /></button>
                </div>
              )}
            />
          </DataTable>
        )}
      </div>

      {!loading && !error && filteredSignatures.length > 0 && (
        <div className="sig__listPaginator">
          <Paginator
            rows={rowsPerPage}
            totalRecords={filteredSignatures.length}
            first={page * rowsPerPage}
            onPageChange={(e) => setPage(Math.floor(e.first / rowsPerPage))}
            template="PrevPageLink PageLinks NextPageLink"
            className="requests-paginator"
          />
        </div>
      )}
    </div>
  );

  // ── Form View ──
  const renderFormView = () => (
    <div className="sig__formPage">
      <div className="sig__formHeader">
        <div className="sig__formHeaderLeft">
          <button className="sig__backBtn" onClick={() => setView('list')}>
            <i className="pi pi-arrow-left" />
          </button>
          <div className="sig__formBreadcrumbWrap">
            <span className="sig__formBreadcrumb">Signatures</span>
            <i className="pi pi-chevron-right sig__formBreadcrumbSep" />
            <h1 className="sig__formTitle">{view === 'edit' ? 'Edit Signature' : 'New Signature'}</h1>
          </div>
        </div>
      </div>

      <div className="sig__formTwoCols">

        <div className="sig__formLeft">

          <div className="sig__formCard">
            <div className="sig__formCardHeader">
              <i className="pi pi-id-card sig__formCardIcon" />
              <span>Signature Details</span>
            </div>
            <div className="sig__field">
              <label className="sig__fieldLabel">Name <span className="sig__req">*</span></label>
              <InputText maxLength={100}
                className="sig__fieldInput"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. John Smith - Agent Signature"
              />
            </div>
          </div>

          {view === 'edit' && (
            <div className="sig__formCard">
              <div className="sig__formCardHeader">
                <i className="pi pi-image sig__formCardIcon" />
                <span>Current Signature</span>
              </div>
              <div className="sig__currentSigWrap">
                {editPreviewLoading ? (
                  <div className="sig__currentSigLoading">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, color: '#0057A4' }} />
                    <span>Loading current signature…</span>
                  </div>
                ) : editPreviewUrl ? (
                  <img src={editPreviewUrl} alt="Current signature" className="sig__currentSigImg" />
                ) : (
                  <span className="sig__previewPlaceholder">No preview available</span>
                )}
              </div>
            </div>
          )}

          <div className="sig__formCard sig__formCard--grow">
            <div className="sig__formCardHeader">
              <i className="pi pi-pencil sig__formCardIcon" />
              <span>{view === 'edit' ? 'Update Signature' : 'Signature'}</span>
            </div>

            <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} className="sig__tabs">

              <TabPanel header="Text Signature">
                <div className="sig__tabContent">
                  <div className="sig__fieldRow2">
                    <div className="sig__field">
                      <label className="sig__fieldLabel">Text</label>
                      <InputText maxLength={500} className="sig__fieldInput" value={textSignature} onChange={(e) => setTextSignature(e.target.value)} placeholder="Enter signature text" />
                    </div>
                    <div className="sig__field">
                      <label className="sig__fieldLabel">Style</label>
                      <Dropdown
                        className="sig__fieldInput"
                        value={textStyle}
                        options={dropdownStyleOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setTextStyle(e.value)}
                        placeholder="Select style"
                        emptyMessage="No available options"
                      />
                    </div>
                  </div>
                  <div className="sig__field">
                    <label className="sig__fieldLabel">Preview</label>
                    <div className="sig__previewBox" style={{ fontFamily: `"${currentFontFamily}", cursive` }}>
                      {textSignature || <span className="sig__previewPlaceholder">Your signature will appear here…</span>}
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel header="Upload Signature">
                <div className="sig__tabContent">
                  <input type="file" accept="image/*" onChange={handleFileChange} id="sig-upload-input" style={{ display: 'none' }} />
                  <label
                    htmlFor="sig-upload-input"
                    className={`sig__uploadBox${isDraggingUpload ? ' sig__uploadBox--dragging' : ''}`}
                    onDragOver={handleUploadDragOver}
                    onDragLeave={handleUploadDragLeave}
                    onDrop={handleUploadDrop}
                  >
                    {uploadPreview ? (
                      <img src={uploadPreview} alt="Signature preview" className="sig__uploadImg" />
                    ) : (
                      <>
                        <i className="pi pi-cloud-upload sig__uploadIcon" />
                        <span className="sig__uploadText">Click or drag & drop to upload</span>
                        <span className="sig__uploadHint">PNG, JPG, SVG — transparent background recommended</span>
                      </>
                    )}
                  </label>
                  {uploadedFile && (
                    <div className="sig__uploadActions">
                      <Button type="button" label="Change" icon="pi pi-upload" className="p-button-outlined p-button-sm sig__outlineBtn" onClick={() => document.getElementById('sig-upload-input')?.click()} />
                      <Button type="button" label="Remove" icon="pi pi-times" className="p-button-text p-button-sm" style={{ color: '#6b7280' }} onClick={() => { setUploadedFile(null); setUploadPreview(''); }} />
                    </div>
                  )}
                </div>
              </TabPanel>

              <TabPanel header="Draw Signature">
                <div className="sig__tabContent">
                  <div className="sig__field">
                    <label className="sig__fieldLabel">Draw your signature below</label>
                    <canvas
                      ref={canvasRef}
                      width={900}
                      height={220}
                      className="sig__canvas"
                      onPointerDown={handleCanvasPointerDown}
                      onPointerMove={handleCanvasPointerMove}
                      onPointerUp={handleCanvasPointerUp}
                      onPointerCancel={handleCanvasPointerUp}
                    />
                  </div>
                  <Button type="button" label="Clear Canvas" icon="pi pi-refresh" className="p-button-outlined p-button-sm sig__outlineBtn" onClick={handleClearCanvas} />
                </div>
              </TabPanel>

            </TabView>
          </div>
        </div>

        <div className="sig__formRight">
          <div className="sig__previewPanel">
            <div className="sig__previewPanelHeader">
              <i className="pi pi-eye sig__previewPanelIcon" />
              <div>
                <div className="sig__previewPanelTitle">Live Preview</div>
                <div className="sig__previewPanelSub">How your signature will appear</div>
              </div>
            </div>

            <div className="sig__previewPanelBody">
              <div className="sig__livePreviewBox">
                {activeTab === 0 && (
                  textSignature
                    ? <span className="sig__livePreviewText" style={{ fontFamily: `"${currentFontFamily}", cursive` }}>{textSignature}</span>
                    : editPreviewUrl && view === 'edit'
                      ? <img src={editPreviewUrl} alt="current signature" className="sig__livePreviewImg" />
                      : <span className="sig__livePreviewEmpty">Enter text to preview…</span>
                )}
                {activeTab === 1 && (
                  uploadPreview
                    ? <img src={uploadPreview} alt="preview" className="sig__livePreviewImg" />
                    : editPreviewUrl && view === 'edit'
                      ? <img src={editPreviewUrl} alt="current signature" className="sig__livePreviewImg" />
                      : <span className="sig__livePreviewEmpty">Upload an image to preview…</span>
                )}
                {activeTab === 2 && (
                  drawDataUrl
                    ? <img src={drawDataUrl} alt="preview" className="sig__livePreviewImg" />
                    : editPreviewUrl && view === 'edit'
                      ? <img src={editPreviewUrl} alt="current signature" className="sig__livePreviewImg" />
                      : <span className="sig__livePreviewEmpty">Draw a signature to preview…</span>
                )}
              </div>

              {name && (
                <div className="sig__previewMeta">
                  <div className="sig__previewMetaRow">
                    <span className="sig__previewMetaLabel">Name</span>
                    <span className="sig__previewMetaValue">{name}</span>
                  </div>
                  <div className="sig__previewMetaRow">
                    <span className="sig__previewMetaLabel">Type</span>
                    <span className="sig__previewMetaValue">
                      {activeTab === 0 ? 'Text' : activeTab === 1 ? 'Image Upload' : 'Hand Drawn'}
                    </span>
                  </div>
                  {activeTab === 0 && currentFontFamily && (
                    <div className="sig__previewMetaRow">
                      <span className="sig__previewMetaLabel">Font Style</span>
                      <span className="sig__previewMetaValue">{currentFontFamily}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="sig__tipsList">
                <div className="sig__tipsHeader"><i className="pi pi-lightbulb" /> Tips</div>
                <ul>
                  <li>Use <strong>Text</strong> for a quick typed signature in various font styles.</li>
                  <li>Use <strong>Upload</strong> for a scanned or PNG signature with transparency.</li>
                  <li>Use <strong>Draw</strong> to sign directly with your mouse or stylus.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="sig__formFooter">
        <Button label="Cancel" icon="pi pi-times" className="p-button-outlined p-button-sm sig__cancelBtn" onClick={handleCancel} disabled={saving} />
        <Button
          label={saving ? 'Saving…' : view === 'edit' ? 'Save Changes' : 'Create Signature'}
          icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          className="p-button-sm sig__primaryBtn"
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </div>
  );

  return (
    <div className="sig__pageWrap">
      <Toast ref={toastRef} position="top-center" />
      <div className="sig__card">
        {view === 'list' ? renderListView() : renderFormView()}
      </div>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        visible={!!deleteTarget}
        onHide={() => { if (!deleting) setDeleteTarget(null); }}
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626', fontSize: 18 }} />
            <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827' }}>Delete Signature</span>
          </div>
        }
        style={{ width: '420px' }}
        modal
        draggable={false}
        resizable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '4px 0' }}>
            <Button
              label="Cancel"
              className="p-button-outlined p-button-sm sig__cancelBtn"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            />
            <Button
              label={deleting ? 'Deleting…' : 'Delete'}
              icon={deleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
              className="p-button-sm p-button-danger"
              onClick={confirmDelete}
              disabled={deleting}
            />
          </div>
        }
      >
        <div style={{ padding: '0.5rem 0', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
          Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
          <br />
          <span style={{ color: '#6b7280', fontSize: 13 }}>This action cannot be undone.</span>
        </div>
      </Dialog>

      <Dialog
        visible={previewModal.open}
        onHide={() => setPreviewModal(prev => ({ ...prev, open: false }))}
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="pi pi-pen-to-square" style={{ color: '#0057A4', fontSize: 16 }} />
            <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827' }}>{previewModal.name}</span>
          </div>
        }
        style={{ width: '480px' }}
        breakpoints={{ '640px': '95vw' }}
        modal
        draggable={false}
        resizable={false}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180, padding: '1rem 0' }}>
          {previewModal.loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, color: '#0057A4' }} />
              <span style={{ fontSize: 13, color: '#6b7280' }}>Loading signature…</span>
            </div>
          ) : previewModal.url ? (
            <div style={{
              background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 12,
              padding: '1.5rem 2rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img
                src={previewModal.url}
                alt={previewModal.name}
                style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: '#9ca3af' }}>
              <i className="pi pi-exclamation-circle" style={{ fontSize: 28, color: '#f59e0b' }} />
              <span style={{ fontSize: 13 }}>Could not load signature preview.</span>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
